import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

/**
 * @typedef {Object} ColumnDef
 * @property {string} field           - key in the data object
 * @property {string} headerName      - column header label
 * @property {boolean} [sortable]     - whether clicking the header sorts this column
 * @property {function} [renderCell]  - optional custom cell renderer (row) => ReactNode
 */

/**
 * @typedef {Object} SortConfig
 * @property {string} field
 * @property {'asc'|'desc'} direction
 */

/**
 * @param {{
 *   columns: ColumnDef[],
 *   data: Object[],
 *   onRowClick?: (row: Object) => void,
 *   emptyMessage?: string,
 *   sortConfig?: SortConfig,
 *   onSort?: (field: string) => void,
 * }} props
 */
export default function DataTable({
  columns,
  data,
  onRowClick,
  emptyMessage = 'No records found.',
  sortConfig,
  onSort,
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const isSorted = sortConfig && sortConfig.field === col.field;
              const sortable = col.sortable && onSort;
              return (
                <TableCell
                  key={col.field}
                  onClick={sortable ? () => onSort(col.field) : undefined}
                  sx={{ backgroundColor: '#eef2f6',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: isSorted ? '#009688' : 'var(--grey-700)',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid var(--grey-200)',
                    py: '12px',
                    px: 2,
                    cursor: sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': sortable ? { color: '#009688' } : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.headerName}
                    {isSorted && (
                      sortConfig.direction === 'asc'
                        ? <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ border: 'none', py: 4 }}>
                <Typography sx={{ color: 'var(--grey-400)', fontSize: '0.875rem', textAlign: 'center' }}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ height: 48,
                  cursor: onRowClick ? 'pointer' : 'default',
                  borderBottom: '1px solid var(--grey-200)',
                  '&:last-child td': { borderBottom: 'none' },
                  '&:hover': onRowClick ? { backgroundColor: '#f8fafc' } : undefined,
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', px: 2, py: 0, border: 'none' }}
                  >
                    {col.renderCell ? col.renderCell(row) : (row[col.field] ?? '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
